import { createContext, useState } from "react";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import {
  getInvoicesService,
  getInvoiceService,
  editInvoiceService,
  deleteInvoiceService,
} from "../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";

export const invoiceAdminContext = createContext({
  codes: [],
  setCodes: () => {},
  invoice: {},
  setInvoice: () => {},
  search: "",
  setSearch: () => {},
  amount: "",
  setAmount: () => {},
  totalCount: null,
  setTotalCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  limit: null,
  setLimit: () => {},
  handleCloseInvoiceModal: () => {},
  handleGetInvoices: () => {},
  handleGetInvoice: () => {},
  handleEditInvoice: () => {},
  handleDeleteInvoice: () => {},
});

const InvoiceAdminContext = ({ children, isUser = false }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const [invoices, setInvoices] = useState([]);
  const [invoice, setInvoice] = useState({});
  const [search, setSearch] = useState("");
  const [amount, setAmount] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [limit, setLimit] = useState(100);

  // Handle Close Invoice Modal
  const handleCloseInvoiceModal = () => {
    setInvoice({});
    setAmount("");
  };

  // Handle Get Invoices Method
  const handleGetInvoices = async (event) => {
    event?.preventDefault();
    const isSearch = !isEmpty(event);

    const objData = {
      search,
      offset: ((isSearch ? 1 : pageNumber) - 1) * limit,
      limit,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getInvoicesService(objData);
      if (status === 200) {
        setInvoices(data.results);
        setTotalCount(data.count);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Invoice Method
  const handleGetInvoice = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getInvoiceService(id);
      if (status === 200) {
        setInvoice(data);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Invoice Method
  const handleEditInvoice = async () => {
    const objData = {
      id: invoice?.id,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editInvoiceService(objData);
      if (status === 200 || status === 201) {
        toastSuccessMessage(
          `فاکتور ${invoice?.client?.first_name} ${invoice?.client?.last_name} با موفقیت ویرایش شد`
        );
        handleCloseInvoiceModal();
        if (!isUser) handleGetInvoices();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      if (!isUser) dispatch(setLoadingFalseAction());
    } finally {
      if (isUser) dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Invoice Method
  const handleDeleteInvoice = async (item) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteInvoiceService(item?.pk);
      if (status === 200) {
        toastSuccessMessage(
          `فاکتور ${item?.order?.client?.first_name} ${item?.order?.client?.last_name} با موفقیت حذف شد`
        );
        handleGetInvoices();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <invoiceAdminContext.Provider
      value={{
        invoices,
        setInvoices,
        invoice,
        setInvoice,
        search,
        setSearch,
        amount,
        setAmount,
        totalCount,
        setTotalCount,
        pageNumber,
        setPageNumber,
        limit,
        setLimit,
        handleCloseInvoiceModal,
        handleGetInvoices,
        handleGetInvoice,
        handleEditInvoice,
        handleDeleteInvoice,
      }}
    >
      {children}
    </invoiceAdminContext.Provider>
  );
};

export default InvoiceAdminContext;
